import React from 'react';
import { toast } from 'sonner';
import { approvalsApi } from '@/services/approvalsApi';
import { timesheetApi } from '@/services/timesheetApi';
import { auditService } from '@/services/auditService';
import { rejectionService } from '@/services/rejectionService';
import { recallTimesheet } from '@/services/timesheetApprovalService';
import type { DataMode } from '@/contexts/DataSourceContext';
import type { Employee, Timesheet, TimesheetEntry } from '@/types/timesheet';
import type { OrganisationTimesheetSettings } from '@/types/timesheetSettings';
import type { RejectionRecord } from '@/types/rejection';

export interface UseTimesheetResubmitParams {
  screenMode: DataMode;
  entries: TimesheetEntry[];
  currentTimesheet: Timesheet;
  currentEmployee: Employee;
  updateTimesheet: (timesheet: Timesheet) => void;
  organizationSettings: OrganisationTimesheetSettings | undefined;
  pendingRejection: RejectionRecord | null;
  hasBlockingPolicyViolation: boolean;
  hasMissingDescriptions: boolean;
  hasMissingCostCentres: boolean;
  isDaily: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface UseTimesheetResubmitResult {
  handleResubmit: () => Promise<void>;
  handleRecallTimesheet: (timesheetId: number, reason: string) => Promise<void>;
}

/** Resubmission of a rejected timesheet and recall of a submitted one. */
export function useTimesheetResubmit({
  screenMode,
  entries,
  currentTimesheet,
  currentEmployee,
  updateTimesheet,
  organizationSettings,
  pendingRejection,
  hasBlockingPolicyViolation,
  hasMissingDescriptions,
  hasMissingCostCentres,
  isDaily,
  isSaving,
  isSubmitting,
  setIsSubmitting,
}: UseTimesheetResubmitParams): UseTimesheetResubmitResult {
  const handleResubmit = async () => {
    if (isSaving || isSubmitting) return;

    if (entries.length === 0) {
      toast.error('Cannot submit empty timesheet');
      return;
    }

    if (hasBlockingPolicyViolation) {
      toast.error('Resubmission blocked by policy', {
        description: 'A policy violation is preventing resubmission. Please review AI Insights for details.',
        duration: 5000,
      });
      return;
    }

    if (organizationSettings?.requireDescription === 'always' && hasMissingDescriptions) {
      toast.error(
        isDaily
          ? 'Please add daily descriptions to all entries before submitting'
          : 'Please add descriptions to all entries'
      );
      return;
    }

    // Validate cost centre requirement on resubmit
    if (hasMissingCostCentres) {
      toast.error('Cannot resubmit timesheet', {
        description: 'All entries must have a cost centre assigned. Please add cost centres to all entries.',
        duration: 5000,
      });
      return;
    }

    // Validate role requirement on resubmit
    if (organizationSettings?.showRole && organizationSettings?.requireRole) {
      const missingRoles = entries.filter(entry => !entry.roleId);
      if (missingRoles.length > 0) {
        toast.error('Cannot resubmit timesheet', {
          description: 'All entries must have a role assigned. Please add roles to all entries.',
          duration: 5000,
        });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      // Update rejection status to resubmitted
      if (pendingRejection) {
        rejectionService.updateRejectionStatus(pendingRejection.id, 'resubmitted');
      }

      if (screenMode === 'live') {
        // LIVE MODE: Save entries first, then use the submit API endpoint
        // which triggers the workflow engine (same pattern as submitTimesheet)
        const timesheetId = currentTimesheet.id;

        // Save the updated entries to the server first
        // The PUT endpoint will also reset status from rejected → draft
        await timesheetApi.updateTimesheet(timesheetId, {
          ...currentTimesheet,
          entries,
        });

        // Now call the submit endpoint to trigger the workflow engine
        const submitResponse = await approvalsApi.submitTimesheet(timesheetId);

        if (submitResponse.message) {
          toast.success(submitResponse.message, {
            description: submitResponse.workflowInstanceId
              ? 'Workflow started successfully'
              : undefined,
            duration: 4000,
          });
        } else {
          toast.success('Timesheet resubmitted for approval');
        }

        // Fetch the updated timesheet from the server to get the latest state
        const updatedTimesheet = await timesheetApi.getTimesheetById(timesheetId);
        if (updatedTimesheet) {
          updateTimesheet(updatedTimesheet);
        }
      } else {
        // DEMO MODE: Update locally (existing behavior)
        const updated = {
          ...currentTimesheet,
          entries,
          status: 'submitted' as const,
          submittedAt: new Date()
        };
        updateTimesheet(updated);
        toast.success('Timesheet resubmitted for approval');
      }
    } catch (error) {
      console.error('Failed to resubmit timesheet:', error);
      toast.error('Failed to resubmit timesheet', {
        description: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecallTimesheet = async (timesheetId: number, reason: string): Promise<void> => {
    // Defensive check: only allow recall when awaiting approval (submitted)
    if (currentTimesheet.status?.toLowerCase() !== 'submitted') {
      toast.error('Cannot recall timesheet', {
        description: `Timesheet is currently ${currentTimesheet.status} and can only be recalled when submitted.`,
      });
      return;
    }
    await recallTimesheet(timesheetId, reason);
    // In demo mode, log a recalled audit entry before the status update
    if (screenMode !== 'live') {
      auditService.logAction(
        timesheetId,
        'recalled',
        currentEmployee.id,
        currentEmployee.name,
        {
          previousStatus: currentTimesheet.status,
          newStatus: 'draft',
          comment: reason,
          actorRole: 'employee',
        }
      );
    }
    // Refresh by updating the timesheet status locally
    const updated = {
      ...currentTimesheet,
      status: 'draft' as const,
    };
    updateTimesheet(updated);
  };

  return { handleResubmit, handleRecallTimesheet };
}
